import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  getPackagesFromDB, 
  addPackageToDB, 
  deletePackageFromDB,
  updatePackageInDB
} from '../services/firebase';

const PackageContext = createContext();

export const PackageProvider = ({ children }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load packages from Firestore
  const loadPackages = async () => {
    setLoading(true);
    setError(null);
    const result = await getPackagesFromDB();
    if (result.success) {
      setPackages(result.data);
    } else {
      setError(result.error);
      console.error('Failed to load packages:', result.error);
    }
    setLoading(false);
  };

  // Add new package to Firestore
  const addPackage = async (newPackage) => {
    setLoading(true);
    const result = await addPackageToDB(newPackage);
    if (result.success) {
      await loadPackages(); // Reload to get updated list
      return { success: true, id: result.id };
    } else {
      setError(result.error);
      setLoading(false);
      return { success: false, error: result.error };
    }
  };

  // Delete package from Firestore
  const deletePackage = async (packageId) => {
    setLoading(true);
    const result = await deletePackageFromDB(packageId);
    if (result.success) {
      await loadPackages(); // Reload to update UI
      return { success: true };
    } else {
      setError(result.error);
      setLoading(false);
      return { success: false, error: result.error };
    }
  };

  // Update existing package
  const updatePackage = async (packageId, updatedData) => {
    setLoading(true);
    const result = await updatePackageInDB(packageId, updatedData);
    if (result.success) {
      await loadPackages();
      return { success: true };
    } else {
      setError(result.error);
      setLoading(false);
      return { success: false, error: result.error };
    }
  };

  // Load packages on component mount
  useEffect(() => {
    loadPackages();
  }, []);

  return (
    <PackageContext.Provider value={{
      packages,
      loading,
      error,
      addPackage,
      deletePackage,
      updatePackage,
      reloadPackages: loadPackages
    }}>
      {children}
    </PackageContext.Provider>
  );
};

export const usePackages = () => {
  const context = useContext(PackageContext);
  if (!context) {
    throw new Error('usePackages must be used within PackageProvider');
  }
  return context;
};